import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight } from "lucide-react";

interface RelatedPostsProps {
  currentPostId: number;
  userInterests?: string[];
}

export function RelatedPosts({ currentPostId, userInterests = [] }: RelatedPostsProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['/api/blog/related', currentPostId, userInterests],
    queryFn: () => {
      const interestsParam = userInterests.length 
        ? `&interests=${userInterests.join(',')}` 
        : '';
      return getQueryFn({ on401: "throw" })(`/api/blog/related?postId=${currentPostId}${interestsParam}`);
    },
    enabled: !!currentPostId
  });
  
  if (isLoading) {
    return (
      <div className="mt-12">
        <h3 className="text-xl font-semibold mb-4">Related Articles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }
  
  const relatedPosts = data?.posts || [];
  
  if (relatedPosts.length === 0) {
    return null;
  }
  
  return (
    <div className="mt-12">
      <h3 className="text-xl font-semibold mb-4">Related Articles</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {relatedPosts.map((post) => (
          <Card key={post.id} className="overflow-hidden hover:shadow-md transition-all">
            <div className="relative h-32 overflow-hidden">
              <img 
                src={post.imageUrl || "https://placehold.co/600x400/png"} 
                alt={post.title} 
                className="w-full h-full object-cover" 
              />
            </div>
            <CardContent className="p-4">
              <Badge variant="outline" className="mb-2">
                {post.category}
              </Badge>
              <h4 className="font-medium line-clamp-2 mb-2">
                {post.title}
              </h4>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-muted-foreground">
                  {new Date(post.publishedAt).toLocaleDateString("en-US", {
                    month: "short", 
                    day: "numeric"
                  })}
                </span>
                <Button asChild variant="ghost" size="sm">
                  <a href={`/blog/${post.slug}`}>
                    Read <ChevronRight className="h-3 w-3 ml-1" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}